import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Baby, CheckCircle, Loader2 } from 'lucide-react';
import { subscriptionAPI } from '../services/api';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('Verifying your subscription...');
  const hasVerified = useRef(false);
  
  let sessionId = searchParams.get('session_id');
  if (sessionId) {
    sessionId = sessionId.replace(/^\{|\}$/g, '');
  }

  const verifySubscription = useCallback(async () => {
    if (!sessionId || hasVerified.current) return;
    
    try {
      hasVerified.current = true;
      const result = await subscriptionAPI.verify(sessionId);
      
      if (result.status === 'active') {
        setStatus('success');
        setMessage('Subscription activated! Redirecting to dashboard...');
        
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        // Poll a few more times
        let attempts = 0;
        const maxAttempts = 5;
        
        const poll = setInterval(async () => {
          attempts++;
          try {
            const pollResult = await subscriptionAPI.verify(sessionId);
            if (pollResult.status === 'active') {
              clearInterval(poll);
              setStatus('success');
              setMessage('Subscription activated! Redirecting to dashboard...');
              setTimeout(() => navigate('/dashboard'), 2000);
            } else if (attempts >= maxAttempts) {
              clearInterval(poll);
              setStatus('success');
              setMessage('Subscription processing. Redirecting to dashboard...');
              setTimeout(() => navigate('/dashboard'), 2000);
            }
          } catch (e) {
            if (attempts >= maxAttempts) {
              clearInterval(poll);
              setStatus('success');
              setMessage('Redirecting to dashboard...');
              setTimeout(() => navigate('/dashboard'), 2000);
            }
          }
        }, 2000);
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('success');
      setMessage('Redirecting to dashboard...');
      setTimeout(() => navigate('/dashboard'), 2000);
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setMessage('Invalid session');
      return;
    }
    verifySubscription();
  }, [sessionId, verifySubscription]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F0] via-[#E5F5FF] to-[#F0FFE5] flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 text-center bg-white/90 backdrop-blur-sm">
        <div className="flex items-center justify-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-[#FFB4A2] to-[#F7DBA7] rounded-full flex items-center justify-center">
            <Baby className="w-8 h-8 text-white" />
          </div>
        </div>
        
        {status === 'verifying' && (
          <>
            <Loader2 className="w-12 h-12 text-[#FFB4A2] animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing Subscription</h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome!</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500">
              You now have access to 2 measurements per month.
            </p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <Button onClick={() => navigate('/subscribe')}>
              Try Again
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
